"use client";

import React from "react";
import { Wrench, Droplets, Flame, Activity, Pipette, ShieldAlert, ArrowRight, PhoneCall, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
const servicesData = [{
  id: 1,
  title: "Emergency Plumbing Services",
  description: "Immediate 24/7 rapid response for active water bursts, major flooding, and urgent residential pipe crises in Jonesboro.",
  icon: "ShieldAlert",
  tag: "24/7 Priority",
  href: "/services/"
}, {
  id: 2,
  title: "Drain Cleaning & Clog Removal",
  description: "High-power hydro jetting and mechanized snaking to clear stubborn blockages, backed-up sinks, and slow main lines.",
  icon: "Sparkles",
  tag: "Rapid Clearing",
  href: "/services/"
}, {
  id: 3,
  title: "Leak Detection & Repair",
  description: "Non-invasive acoustic and thermal diagnostics that pinpoint hidden slab leaks and behind-wall pipe failures instantly.",
  icon: "Droplets",
  tag: "Zero Guesswork",
  href: "/services/"
}, {
  id: 4,
  title: "Water Heater Repair & Installation",
  description: "Fast diagnostics, element replacements, and full unit installations for tankless and conventional water heaters.",
  icon: "Flame",
  tag: "Same-Day Fix",
  href: "/water-heater/"
}, {
  id: 5,
  title: "Pipe Repair & Repiping",
  description: "Precision sectional pipe patching and complete copper or PEX replacements for corroded or burst plumbing systems.",
  icon: "Wrench",
  tag: "Permanent Fix",
  href: "/services/"
}, {
  id: 6,
  title: "Sewer Line Inspection & Repair",
  description: "High-definition video camera inspections and trenchless sewer line repairs to stop tree roots and line collapses.",
  icon: "Activity",
  tag: "HD Camera Diagnosis",
  href: "/services/"
}];
export default function PlumbingServices() {
  const shouldReduceMotion = useReducedMotion();
  const getServiceIcon = (iconName: string) => {
    switch (iconName) {
      case "ShieldAlert":
        return <ShieldAlert className="size-6 text-primary" />;
      case "Sparkles":
        return <Sparkles className="size-6 text-primary" />;
      case "Droplets":
        return <Droplets className="size-6 text-primary" />;
      case "Flame":
        return <Flame className="size-6 text-primary" />;
      case "Wrench":
        return <Wrench className="size-6 text-primary" />;
      case "Activity":
        return <Activity className="size-6 text-primary" />;
      default:
        return <Wrench className="size-6 text-primary" />;
    }
  };
  return <section id="plumbing-services" data-nav="light" className="bg-background text-foreground py-16 md:py-24 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-10">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-12 md:pb-16 border-b border-border">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs md:text-sm font-bold tracking-wide uppercase mb-4">
              <span className="inline-block size-2 rounded-full bg-accent" />
              Jonesboro Diagnostic Services
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-foreground tracking-tight leading-[1.15]">
              Full-Scale Emergency &amp; Everyday Plumbing Solutions
            </h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              When water is where it shouldn't be, every minute counts. We triage, diagnose, and resolve urgent residential plumbing issues with upfront pricing and zero delays.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
            <Button asChild className="bg-accent text-accent-foreground font-bold hover:brightness-105 rounded-full px-6 py-5 shadow-sm transition-[transform,filter]">
              <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                <PhoneCall className="size-4" />
                <span>Call (470) 847-9076</span>
              </a>
            </Button>
            <Button asChild variant="outline" className="border-border text-foreground hover:bg-muted rounded-full px-6 py-5 font-semibold transition-colors">
              <Link to="/services/" className="flex items-center justify-center gap-2">
                <span>View All Services</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-12 md:pt-16">
          {servicesData.map((service, index) => <div key={service.id} data-index={index}>
              <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.35,
            delay: index * 0.06,
            ease: "easeOut"
          }} className="h-full">
                <Card className="h-full border border-border bg-card hover:border-primary/40 transition-[border-color,box-shadow] duration-200 hover:shadow-md flex flex-col justify-between relative group">
                  {/* Decorative Left Border Accent */}
                  <div className="absolute left-0 top-6 bottom-6 w-[3px] bg-accent rounded-r-sm opacity-0 group-hover:opacity-100 transition-opacity" />

                  <div>
                    <CardHeader className="pb-4">
                      <div className="flex items-center justify-between gap-4 mb-4">
                        <div className="size-12 rounded-xl bg-secondary flex items-center justify-center border border-border">
                          {getServiceIcon(service.icon)}
                        </div>
                        <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-muted text-muted-foreground border border-border">
                          {service.tag}
                        </span>
                      </div>
                      <CardTitle className="text-xl sm:text-2xl font-bold text-foreground group-hover:text-primary transition-colors">
                        {service.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                        {service.description}
                      </CardDescription>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-4 border-t border-border">
                    <Button asChild variant="ghost" className="w-full justify-between px-0 font-bold text-primary hover:text-primary hover:bg-transparent">
                      <Link to={service.href} className="inline-flex items-center justify-between w-full">
                        <span>Service Details &amp; Rates</span>
                        <ArrowRight className="size-4 ml-2 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            </div>)}
        </div>

        {/* Rapid Trust Verification Strip */}
        <div className="mt-12 sm:mt-16 rounded-2xl bg-secondary/50 border border-border p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 text-center sm:text-left">
            <div className="size-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <CheckCircle2 className="size-5" />
            </div>
            <div>
              <h4 className="text-base sm:text-lg font-bold text-foreground">
                Need an immediate diagnostic check?
              </h4>
              <p className="text-sm text-muted-foreground mt-0.5">
                Our licensed Jonesboro plumbers are on standby 24/7 with fully equipped service vehicles.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <Button asChild className="bg-primary text-primary-foreground font-bold hover:bg-primary/90 rounded-full px-6 py-5 shadow-xs transition-[transform,background-color]">
              <a href="tel:4708479076" className="flex items-center gap-2">
                <PhoneCall className="size-4" />
                <span>Dispatch Tech: (470) 847-9076</span>
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}